import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Circle, MagnifyingGlass, ArrowsClockwise, Users,
    Stethoscope, Headset, Briefcase, Crown, ShieldCheck, UserCircle,
    CaretRight,
} from "@phosphor-icons/react";

/* ---- Constants ------------------------------------------------------- */

const ROLE_META = {
    admin:         { label: "Admin",       icon: ShieldCheck,  tone: "hsl(0 75% 55%)" },
    super_admin:   { label: "Super admin", icon: Crown,        tone: "hsl(48 96% 45%)" },
    doctor:        { label: "Provider",    icon: Stethoscope,  tone: "hsl(173 58% 39%)" },
    clinic_staff:  { label: "Clinic staff", icon: Stethoscope, tone: "hsl(173 40% 55%)" },
    patient:       { label: "Patient",     icon: Users,        tone: "hsl(192 100% 31%)" },
    sales:         { label: "Sales",       icon: Briefcase,    tone: "hsl(280 60% 55%)" },
    // Everything under COE — planners, QA, technicians, production, support etc.
    // are surfaced as their own bucket by staff_type.
    planner:       { label: "Planner",     icon: Headset,      tone: "hsl(200 80% 50%)" },
    qa:            { label: "QA",          icon: Headset,      tone: "hsl(200 70% 45%)" },
    technician:    { label: "Technician",  icon: Headset,      tone: "hsl(200 60% 40%)" },
    production:    { label: "Production",  icon: Headset,      tone: "hsl(27 87% 50%)" },
    support:       { label: "Support",     icon: Headset,      tone: "hsl(340 75% 55%)" },
    counsellor:    { label: "Counsellor",  icon: Headset,      tone: "hsl(340 60% 55%)" },
    coe_lead:      { label: "COE lead",    icon: Headset,      tone: "hsl(280 60% 55%)" },
    internal_dentist: { label: "In-house dentist", icon: Stethoscope, tone: "hsl(150 60% 40%)" },
};

const STATUS_META = {
    online:  { label: "Online",  dot: "bg-emerald-500", ring: "ring-emerald-200", text: "text-emerald-700" },
    idle:    { label: "Idle",    dot: "bg-amber-400",   ring: "ring-amber-200",   text: "text-amber-700" },
    offline: { label: "Offline", dot: "bg-slate-300",   ring: "ring-slate-100",   text: "text-slate-500" },
};

/* ---- Utils ----------------------------------------------------------- */

function relTime(iso) {
    if (!iso) return "never";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

/**
 * profileHrefFor(user)
 * -------------------
 * Resolves the deep-link an admin should land on when they click a
 * presence row. Returns null when this role has no per-user profile page
 * (in which case the row renders as a plain, non-clickable div).
 */
function profileHrefFor(u) {
    if (!u) return null;
    switch (u.role) {
        case "doctor":
            // /admin/providers/:id expects the doctor entity id, not the user id.
            return u.doctor_id ? `/admin/providers/${u.doctor_id}` : null;
        case "clinic_staff":
            // Clinic staff don't have their own profile page — bounce to
            // their parent doctor's profile.
            return u.parent_doctor_id ? `/admin/providers/${u.parent_doctor_id}` : null;
        case "patient":
            // For patients, users.id === patient_id (see admin_get_patient).
            return `/admin/patients/${u.id}`;
        case "staff":
            // All COE staff sub-types share the same profile route.
            return `/admin/coe/people/${u.id}`;
        case "super_admin":
            return "/super-admin";
        case "admin":
            // Option B (per user choice 2026-07-07): Admins → COE reports
            // (there's no dedicated admin-profile page yet).
            return "/admin/coe/reports";
        case "sales":
        case "sales_manager":
            // Option B: Sales / Sales manager → sales portal.
            return "/admin/sales";
        default:
            return null;
    }
}

/* ---- Component ------------------------------------------------------- */

export default function AdminPresence() {
    const [data, setData] = useState({ items: [], counts: { total: 0, online: 0, idle: 0, offline: 0, by_role: {} } });
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [roleFilter, setRoleFilter] = useState("all");        // 'all' | role key
    const [statusFilter, setStatusFilter] = useState("all");    // 'all' | 'online' | 'idle' | 'offline'
    const [q, setQ] = useState("");

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        setRefreshing(true);
        try {
            const params = {};
            if (roleFilter !== "all") params.role = roleFilter;
            if (statusFilter !== "all") params.status = statusFilter;
            if (q.trim()) params.q = q.trim();
            const { data } = await api.get("/admin/presence", { params });
            setData(data);
        } catch {
            /* silent */
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [roleFilter, statusFilter, q]);

    // Initial + on-filter-change fetch.
    useEffect(() => { load(); }, [load]);

    // Auto-refresh every 30s (silent — no loading spinner flicker).
    useEffect(() => {
        const t = setInterval(() => load(true), 30_000);
        return () => clearInterval(t);
    }, [load]);

    const counts = data.counts || { total: 0, online: 0, idle: 0, offline: 0, by_role: {} };
    const items = data.items || [];

    // Build the role tabs from the actual data (so we don't show a
    // "Sales" tab if there are 0 sales users seeded).
    const roleTabs = useMemo(() => {
        const entries = Object.entries(counts.by_role || {});
        entries.sort((a, b) => b[1].total - a[1].total);
        return entries.map(([role, c]) => ({ role, ...c }));
    }, [counts]);

    return (
        <div data-testid="admin-presence-page">
            <div className="mb-8">
                <div className="overline text-primary mb-2">Real-time</div>
                <h1 className="font-display text-3xl sm:text-4xl font-bold">Who&apos;s online</h1>
                <p className="text-muted-foreground mt-1 text-sm">
                    Live presence across every user on Invisique™ — patients, providers, COE staff, sales and admin.
                    Refreshes every 30 seconds.
                </p>
            </div>

            {/* Summary strip — clickable filters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <StatCard
                    label="Online now"
                    value={counts.online}
                    status="online"
                    active={statusFilter === "online"}
                    onClick={() => setStatusFilter(statusFilter === "online" ? "all" : "online")}
                    testid="presence-count-online"
                />
                <StatCard
                    label="Idle"
                    value={counts.idle}
                    status="idle"
                    active={statusFilter === "idle"}
                    onClick={() => setStatusFilter(statusFilter === "idle" ? "all" : "idle")}
                    testid="presence-count-idle"
                />
                <StatCard
                    label="Offline"
                    value={counts.offline}
                    status="offline"
                    active={statusFilter === "offline"}
                    onClick={() => setStatusFilter(statusFilter === "offline" ? "all" : "offline")}
                    testid="presence-count-offline"
                />
                <StatCard
                    label="Total active users"
                    value={counts.total}
                    status="total"
                    active={statusFilter === "all"}
                    onClick={() => setStatusFilter("all")}
                    testid="presence-count-total"
                />
            </div>

            {/* Filter bar */}
            <div className="bg-card border border-border rounded-2xl p-4 mb-4 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[220px]">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by name, email or phone"
                        className="pl-9"
                        data-testid="presence-search-input"
                    />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {["all", "online", "idle", "offline"].map((s) => {
                        const active = statusFilter === s;
                        const meta = STATUS_META[s];
                        return (
                            <button
                                key={s}
                                onClick={() => setStatusFilter(s)}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border hover:border-primary/40"}`}
                                data-testid={`presence-status-filter-${s}`}
                            >
                                {s === "all" ? "All statuses" : (meta?.label || s)}
                                {s !== "all" && ` · ${counts[s] ?? 0}`}
                            </button>
                        );
                    })}
                </div>

                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => load()}
                    disabled={refreshing}
                    data-testid="presence-refresh-btn"
                >
                    <ArrowsClockwise size={14} className={refreshing ? "animate-spin" : ""} />
                    Refresh
                </Button>
            </div>

            {/* Role tabs — sticky so they stay visible while scrolling the
                long user list. `top-20` sits just below the sticky admin
                header (h-16 + a little breathing room). */}
            <div className="sticky top-16 z-30 -mx-1 px-1 py-2 mb-4 bg-secondary/60 backdrop-blur-md rounded-2xl">
                <div className="flex flex-wrap gap-2">
                    <RoleTab
                        active={roleFilter === "all"}
                        onClick={() => setRoleFilter("all")}
                        label="All roles"
                        total={counts.total}
                        online={counts.online}
                        tone="hsl(192 100% 31%)"
                        Icon={UserCircle}
                        testid="presence-role-tab-all"
                    />
                    {roleTabs.map(({ role, total, online }) => {
                        const meta = ROLE_META[role] || { label: role, icon: UserCircle, tone: "hsl(220 15% 45%)" };
                        return (
                            <RoleTab
                                key={role}
                                active={roleFilter === role}
                                onClick={() => setRoleFilter(role)}
                                label={meta.label}
                                total={total}
                                online={online}
                                tone={meta.tone}
                                Icon={meta.icon}
                                testid={`presence-role-tab-${role}`}
                            />
                        );
                    })}
                </div>
            </div>

            {/* Users list */}
            <div className="bg-card border border-border rounded-2xl divide-y divide-border overflow-hidden" data-testid="presence-user-list">
                {loading && (
                    <div className="p-6 text-sm text-muted-foreground">Loading presence…</div>
                )}
                {!loading && items.length === 0 && (
                    <div className="p-8 text-sm text-muted-foreground text-center">
                        No users match the current filters.
                    </div>
                )}
                {!loading && items.map((u) => (
                    <UserRow key={u.id} user={u} />
                ))}
            </div>
        </div>
    );
}

/* ---- Sub-components -------------------------------------------------- */

function StatCard({ label, value, status, active, onClick, testid }) {
    const meta = STATUS_META[status] || null;
    // Accent colour used both for the active ring and the pulsing dot
    const ringColor = {
        online:  "ring-emerald-400 bg-emerald-50/40",
        idle:    "ring-amber-400 bg-amber-50/40",
        offline: "ring-slate-400 bg-slate-50/40",
        total:   "ring-primary bg-primary/5",
    }[status] || "ring-primary bg-primary/5";
    return (
        <button
            type="button"
            onClick={onClick}
            className={`text-left p-5 bg-card border border-border rounded-2xl transition-all hover:border-primary/40 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${active ? `ring-2 ${ringColor}` : ""}`}
            data-testid={testid}
            aria-pressed={active}
        >
            <div className="flex items-center gap-2">
                {meta && <span className={`inline-block h-2.5 w-2.5 rounded-full ${meta.dot}`} />}
                <div className="text-xs text-muted-foreground">{label}</div>
            </div>
            <div className="mt-2 font-display text-3xl font-bold">{value ?? 0}</div>
            <div className="mt-1 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground/70">
                {active ? "Filtering" : "Click to filter"}
            </div>
        </button>
    );
}

function RoleTab({ active, onClick, label, total, online, tone, Icon, testid }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-colors ${active ? "bg-primary/10 border-primary/50 text-primary" : "bg-white border-border hover:border-primary/40"}`}
            data-testid={testid}
        >
            <Icon size={16} weight="duotone" color={tone} />
            <span className="font-medium">{label}</span>
            <span className="text-xs text-muted-foreground">
                {online}<span className="opacity-50">/{total}</span>
            </span>
        </button>
    );
}

function UserRow({ user }) {
    const meta = STATUS_META[user.status] || STATUS_META.offline;
    const roleMeta = ROLE_META[user.staff_type || user.role] || ROLE_META[user.role] || { label: user.role, tone: "hsl(220 15% 45%)" };
    const RoleIcon = roleMeta.icon || UserCircle;
    const href = profileHrefFor(user);

    // Body is identical regardless of clickability — only the wrapper
    // element changes. Keeps the DOM diff minimal.
    const body = (
        <>
            {/* Avatar + status dot */}
            <div className="relative shrink-0">
                {user.avatar_url ? (
                    <img
                        src={user.avatar_url}
                        alt={user.name}
                        className="h-11 w-11 rounded-full object-cover ring-1 ring-border"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                ) : (
                    <div className="h-11 w-11 rounded-full bg-secondary flex items-center justify-center text-sm font-semibold text-muted-foreground">
                        {(user.name || "?").charAt(0).toUpperCase()}
                    </div>
                )}
                <span
                    className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-white ${meta.dot}`}
                    aria-label={meta.label}
                    title={meta.label}
                />
            </div>

            {/* Name / meta */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium truncate">{user.name}</div>
                    {user.is_super_admin && (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100" variant="outline">
                            <Crown size={11} className="mr-1" /> Super admin
                        </Badge>
                    )}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                    <RoleIcon size={11} weight="duotone" color={roleMeta.tone} className="inline mr-1 -mt-0.5" />
                    {roleMeta.label}
                    {user.clinic_name && <span> · {user.clinic_name}</span>}
                    {user.city && <span> · {user.city}</span>}
                    {user.email && <span> · {user.email}</span>}
                </div>
            </div>

            {/* Status + last seen */}
            <div className="text-right shrink-0">
                <div className={`text-xs font-semibold ${meta.text} flex items-center gap-1.5 justify-end`}>
                    <Circle size={8} weight="fill" className={meta.text} />
                    {meta.label}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                    {user.status === "online" ? "active now" : relTime(user.last_seen_at)}
                </div>
                {user.status === "online" && user.last_seen_route && (
                    <div className="text-[11px] text-muted-foreground mt-0.5 max-w-[220px] truncate" title={user.last_seen_route}>
                        on <span className="font-mono">{user.last_seen_route}</span>
                    </div>
                )}
            </div>

            {/* Deep-link chevron — only for clickable rows */}
            {href && (
                <CaretRight
                    size={16}
                    className="shrink-0 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all"
                />
            )}
        </>
    );

    if (href) {
        return (
            <Link
                to={href}
                className="group flex items-center gap-4 p-4 hover:bg-secondary/60 transition-colors"
                data-testid={`presence-user-${user.id}`}
                aria-label={`Open ${user.name} profile`}
            >
                {body}
            </Link>
        );
    }

    return (
        <div
            className="flex items-center gap-4 p-4 opacity-95 cursor-default"
            data-testid={`presence-user-${user.id}`}
            title="No profile page available for this role yet"
        >
            {body}
        </div>
    );
}
